import React from "react"
import { connect } from "react-redux"
import classNames from "classnames"
import PropTypes from "prop-types"
import swal from "sweetalert"

import {
    addColumn,
    clearFilter,
    clearSort,
    renameColumn,
    removeColumn,
    sortByColumnAtIndex,
    toggleColumnIsHiddenOnPublic,
    toggleDefaultSortOnColumn,
} from "app/static/frontend/sheets/sheet-action-creators"
import { headerCellConnection } from "app/static/frontend/sheets/selectors/column-selectors"
import { extractIdFromSheetKey } from "app/static/frontend/sheets/selectors/sheet-selectors"

import RemoveColumnButton from "app/static/frontend/sheets/components/RemoveColumnButton"
import ColumnName from "app/static/frontend/sheets/components/ColumnName"
import ColumnSearchTrigger from "app/static/frontend/sheets/components/ColumnSearchTrigger"
import AddColumnButton from "app/static/frontend/sheets/components/AddColumnButton"
import CustomColumnColorButton from "app/static/frontend/sheets/components/CustomColumnColorButton"
import ColumnSortingIndicator from "app/static/frontend/sheets/components/ColumnSortingIndicator"
import DefaultColumnSortingIndicator from "app/static/frontend/sheets/components/DefaultColumnSortingIndicator"
import HideColumnTrigger from "app/static/frontend/sheets/components/HideColumnTrigger"
import Icon from "app/static/frontend/components/Icon"
import Tooltip from "app/static/frontend/components/Tooltip"
import { errorText, defaultSortingIndicatorText } from "app/static/frontend/sheets/components/helpTexts"
import sortTypes from "app/static/frontend/sheets/sort-types"

import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

// This component renders the Header of regular columns in Sheets
export const HeaderCell = ({
    // props from the sheet
    canFitInMemory,
    isReorderable,
    canAddColumns,
    canHideColumns,
    canSearch,
    canSort,
    canRenameColumns,
    canRemoveColumns,
    canSetDefaultSort,
    id,
    canCustomColorColumn,
    isPublicSheetMode,

    // props pre-processed by render-column
    columnId,
    columnIndex,

    columnField,
    columnName,
    canSortOnBackend,
    canFilterOnBackend,
    columnCustomErrorMessage,
    columnHasError,

    // clear filtering action creator
    clearFilter,

    headerCellStyles,
    // is this column hidden from public sheets
    isHidden,

    // the enum value (SheetSortType) corresponding to the sort type
    defaultSortType,
    sortType,

    // boolean toggles
    isSearching,
    sorting,
    derived,
    isDefaultSorting,
    isDefaultSortingAndCanSetDefaultSort,
    defaultSortingType,
    // action creators
    renameColumn,
    removeColumn,
    columnWidth,
    ...props
}) => {
    const renderHideColumnsTrigger = () => (
        <HideColumnTrigger
            columnId={columnId}
            handleHideColumn={props.toggleColumnIsHiddenOnPublic}
            isHidden={isHidden}
            sheetKey={id}
        />
    )

    const renderSearchTrigger = () => (
        <ColumnSearchTrigger canSearch={canSearch} id={id} columnIndex={columnIndex} columnField={columnField} />
    )

    // function that renders the clear filters button for the header columns
    const renderClearSearch = () => (
        <Tooltip label="Click to Clear Filtering">
            <div>
                <Icon
                    dataCy="clear-filtering"
                    name={"fa-times-circle-o"}
                    onClick={() => clearFilter(id, columnIndex)}
                />
            </div>
        </Tooltip>
    )

    const renderDefaultSortColumnsTrigger = () => (
        <DefaultColumnSortingIndicator
            columnId={columnId}
            defaultSortType={defaultSortType}
            id={id}
            toggleDefaultSortOnColumn={props.toggleDefaultSortOnColumn}
        />
    )

    const renderSortingIndicator = () => (
        <ColumnSortingIndicator
            canSort={canSort}
            clearSort={props.clearSort}
            columnIndex={columnIndex}
            columnId={columnId}
            id={id}
            sortByColumnAtIndex={props.sortByColumnAtIndex}
            sorting={sorting}
            sortType={sortType}
            canSetDefaultSort={canSetDefaultSort}
            isDefaultSorting={isDefaultSorting}
            defaultSortingType={defaultSortingType}
            toggleDefaultSortOnColumn={props.toggleDefaultSortOnColumn}
        />
    )

    const renderDefaultSortingIndicator = () => (
        <span className="default-sorting-indicator">
            <Tooltip label={defaultSortingIndicatorText}>
                <div>
                    <Icon name={"thumb-tack"} onClick={() => props.toggleDefaultSortOnColumn(id, columnId, 0)} />
                </div>
            </Tooltip>
            <Icon name={defaultSortType === sortTypes.ASC ? "sort-amount-asc" : "sort-amount-desc"} />
        </span>
    )

    const handleRemoveColumn = () => removeColumn(id, columnId, columnIndex)

    const handleRenameColumn = () => {
        if (canRenameColumns) {
            swal({
                title: "Rename Column",
                content: {
                    element: "input",
                    attributes: {
                        placeholder: "Rename Column...",
                        value: columnName,
                    },
                },
                buttons: [true, true],
                closeOnClickOutside: false,
            }) // in this case we don't need to check for inputValue === ""
                .then((inputValue) => (inputValue ? renameColumn(id, columnId, inputValue) : null))
        }
    }

    return (
        <div className={classNames("header-cell-inner")}>
            <span
                className={classNames(
                    "column-name",
                    "column-title",
                    { "shift-right": isReorderable },
                    { "column-name-pointer": canRenameColumns },
                )}
            >
                {/* Show the name of the column with a default so that if they forget to name it they still have a click target to rename it*/}
                <ColumnName
                    columnName={columnName}
                    canRenameColumns={canRenameColumns}
                    handleRenameColumn={handleRenameColumn}
                />
                {isDefaultSortingAndCanSetDefaultSort && renderDefaultSortingIndicator()}
            </span>
            <span className="active-column-options">
                {columnHasError && (
                    <Tooltip label={columnCustomErrorMessage || errorText}>
                        <div>
                            <Icon name="exclamation-circle" onClick={handleRemoveColumn} />
                        </div>
                    </Tooltip>
                )}
                {isSearching && renderSearchTrigger()}

                {/* Render the button to clear filtering */}
                {isSearching && renderClearSearch()}

                {sorting && renderSortingIndicator()}
            </span>

            <div className="column-options" data-cy={`column-${columnName}`} style={headerCellStyles.columnOptions}>
                <div data-cy="column-triangle" className="column-triangle" />
                <div className="column-icons" data-cy="column-icons" style={headerCellStyles.columnIcons}>
                    {canCustomColorColumn && !isPublicSheetMode && (
                        <CustomColumnColorButton sheetId={extractIdFromSheetKey(id)} columnIndex={columnIndex} />
                    )}
                    {canHideColumns && renderHideColumnsTrigger()}

                    {renderSortingIndicator()}
                    {renderSearchTrigger()}
                    {canRemoveColumns && (
                        <RemoveColumnButton
                            columnIndex={columnIndex}
                            columnId={columnId}
                            id={id}
                            removeColumn={removeColumn}
                        />
                    )}
                    {canAddColumns && <AddColumnButton sheetId={extractIdFromSheetKey(id)} columnIndex={columnIndex} />}
                </div>
            </div>
        </div>
    )
}

HeaderCell.propTypes = {
    // the key in the store of the sheet
    id: PropTypes.string.isRequired,
    canAddColumns: PropTypes.bool,
    canRemoveColumns: PropTypes.bool,
    canRenameColumns: PropTypes.bool,
    derived: PropTypes.bool,
    isReorderable: PropTypes.bool,
    isSearching: PropTypes.bool,
    sheetId: PropTypes.string.isRequired,
    sorting: PropTypes.bool,
}

HeaderCell.defaultProps = {
    canRemoveColumns: false,
    canAddColumns: false,
}

export default connect((state, props) => headerCellConnection(state, props), {
    addColumn,
    clearFilter,
    clearSort,
    removeColumn,
    renameColumn,
    sortByColumnAtIndex,
    toggleColumnIsHiddenOnPublic,
    toggleDefaultSortOnColumn,
})(HeaderCell)
